import { FGListen, FGSelectInput, FGTextInput } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { DogPedigreeApi, SaveNewPedigreeDto } from "../../../../../../api";
import {
  FGCountrySelect,
  FGSRSHDateMaskInput,
  SmallFormGenerator,
} from "../../../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

export interface IDogPedigreePanelProps {
  dogId: number;
  onSaved: () => void;
  pedigreeId: number;
}

export const DogPedigreePanel: React.FunctionComponent<IDogPedigreePanelProps> = ({
  dogId,
  onSaved,
  pedigreeId,
}) => {
  const { t } = useTl();
  const api = useApiService(DogPedigreeApi);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      countryId: Yup.number().nullable().required(t(ETLCodes.Required)),
      countryPedigreePrefixId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required)),
      identifier: Yup.string().required(t(ETLCodes.Required)), //backend validation
    });
  }, [t]);

  const { data, saveItem, saving } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: (currentId = pedigreeId) =>
          currentId > 0
            ? api.getDogPedigree(currentId)
            : new SaveNewPedigreeDto({
                dogId: dogId,
                attributionDate: new Date(),
              }).toJSON(),
        saveApiFn: async (d: SaveNewPedigreeDto) => {
          const res = await api.addOrEditPedigree(d);
          onSaved();
          return res;
        },
        deleteApiFn: (d) => {},
      }),
      [api, dogId, onSaved, pedigreeId]
    ),
    [pedigreeId]
  );
  const [CountryId, setCountryId] = React.useState<number>(data?.countryId);

  const [prefixes, ploading] = useReferential(
    (a) =>
      CountryId
        ? a.getPedigreePrefixesByCountry(CountryId, !pedigreeId ? dogId : 0)
        : null,
    true,
    [CountryId, dogId]
  );

  return (
    <SmallFormGenerator
      onSubmit={saveItem}
      initialValues={data}
      editMode={true}
      validationSchema={FormSchema}
      minLabelWidth={150}
      saving={saving}
    >
      <FGListen field="countryId" onChanged={setCountryId} />
      <FGCountrySelect
        name="countryId"
        includeBelgium={false}
        label={t(ETLCodes.Country)}
        readonly={!!pedigreeId}
      />
      <FGSelectInput
        name="countryPedigreePrefixId"
        items={prefixes}
        loading={ploading}
        label={t(ETLCodes.Prefix)}
        readonly={!!pedigreeId}
      />
      <FGTextInput label={t(ETLCodes.PedigreeNo)} name="identifier" />
      <FGSRSHDateMaskInput
        name="attributionDate"
        label={t(ETLCodes.AttributionDate)}
      />
    </SmallFormGenerator>
  );
};
